import React, { createContext, useContext, useEffect, useState } from "react";
import { track } from "@/lib/api";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const KEY = "aether_cart";
const uid = () => Math.random().toString(36).slice(2, 10);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, size, qty = 1) => {
    const key = `${product.product_id}:${size}`;
    track("add_to_cart", { product_id: product.product_id });
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.key === key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: Math.min(10, copy[idx].qty + qty) };
        return copy;
      }
      return [...prev, {
        key, product_id: product.product_id, name: product.name, price: product.price,
        image: product.image, type: product.type, accent: product.accent, size, qty,
        custom: false,
      }];
    });
  };

  // Custom design from the Creative Design Studio (front and optional back print)
  const addCustom = (item) => {
    track("add_to_cart", { product_id: `custom:${item.blank_id}`, design_id: item.design_id });
    setItems((prev) => [...prev, {
      key: `custom:${uid()}`,
      product_id: `custom:${item.blank_id}`,
      custom: true,
      blank_id: item.blank_id,
      design_id: item.design_id,
      design_url: item.design_url,
      name: item.name,
      type: item.type,
      price: item.price,
      image: item.image,
      image_back: item.image_back,
      accent: item.accent || "#00E5FF",
      color: item.color,
      color_hex: item.color_hex,
      placement: item.placement,
      // Back-print bundle
      design_id_back: item.design_id_back,
      design_url_back: item.design_url_back,
      placement_back: item.placement_back,
      has_back: !!item.design_url_back,
      size: item.size,
      qty: Math.max(1, Math.min(10, item.qty || 1)),
    }]);
  };

  const remove = (key) => setItems((prev) => prev.filter((i) => i.key !== key));

  const setQty = (key, qty) =>
    setItems((prev) => prev.map((i) =>
      i.key === key ? { ...i, qty: Math.max(1, Math.min(10, qty)) } : i));

  const clear = () => setItems([]);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const total = items.reduce((s, i) => s + i.price * i.qty, 0);

  // Server payload (carries custom-design fields through to checkout/quote)
  const payload = items.map((i) => i.custom ? {
    product_id: i.product_id, size: i.size, qty: i.qty, custom: true,
    blank_id: i.blank_id, design_id: i.design_id, design_url: i.design_url,
    color: i.color, color_hex: i.color_hex, name: i.name, placement: i.placement,
    design_id_back: i.design_id_back, design_url_back: i.design_url_back, placement_back: i.placement_back,
  } : { product_id: i.product_id, size: i.size, qty: i.qty });

  return (
    <CartContext.Provider value={{ items, add, addCustom, remove, setQty, clear, count, total, payload }}>
      {children}
    </CartContext.Provider>
  );
}
